import React, { useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FiDownload, FiRefreshCw } from 'react-icons/fi';
import { HiDocumentReport } from 'react-icons/hi';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import SpinnerCargando from '../util/SpinnerCargando';
import InputClientes from '../util/Inputs/InputClientes';
import PlantillaModal from '../util/PlantillaModal';
import TablaEntidad from '../util/tablas/TablaEntidad';
import generarPDFEstadoCuentaCliente from './generarPDFEstadoCuentaCliente';

function hoyLocal() {
  const hoy = new Date();
  const yyyy = hoy.getFullYear();
  const mm = String(hoy.getMonth() + 1).padStart(2, '0');
  const dd = String(hoy.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatMonto(monto) {
  return parseFloat(monto || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatFecha(fecha) {
  if (!fecha) return '-';
  return new Date(fecha).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' });
}

const ETIQUETAS_TIPO = {
  factura: 'Factura',
  pago: 'Pago',
  nota_credito: 'Nota de Crédito'
};

function TarjetaMetrica({ titulo, valor, colorBorde, colorTexto }) {
  return (
    <div className={`bg-white rounded-xl border shadow-md p-4 border-l-4 ${colorBorde}`}>
      <p className="text-sm text-gray-600 font-medium mb-1">{titulo}</p>
      <p className={`text-xl font-bold ${colorTexto}`}>{valor}</p>
    </div>
  );
}

export default function EstadoCuentaCliente() {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [mostrarModalClientes, setMostrarModalClientes] = useState(false);
  const [cliente, setCliente] = useState({ id_entidad_cliente: '', nombre: '' });
  const [filtros, setFiltros] = useState({ fecha_desde: '', fecha_hasta: hoyLocal(), solo_con_comprobante: false });

  const cargarDatos = useCallback(async (idCliente) => {
    if (!idCliente) return;
    try {
      setCargando(true);
      const params = Object.fromEntries(Object.entries(filtros).filter(([, v]) => v !== '' && v !== false));
      const response = await instanciaAxios.get(`/reporte/estado-cuenta-cliente/${idCliente}`, { params });
      setDatos(response.data);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al cargar el estado de cuenta del cliente:', error);
      toast.error('Error al cargar el estado de cuenta del cliente');
      setDatos(null);
    } finally {
      setCargando(false);
    }
  }, [filtros]);

  const handleClienteSeleccionado = (entidad) => {
    setMostrarModalClientes(false);
    if (!entidad) return;
    const nombre = entidad.razon_social || `${entidad.nombre} ${entidad.apellido || ''}`.trim();
    setCliente({ id_entidad_cliente: entidad.id_entidad, nombre });
    cargarDatos(entidad.id_entidad);
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  const handleActualizar = () => {
    if (cliente.id_entidad_cliente) cargarDatos(cliente.id_entidad_cliente);
  };

  const handleExportarPDF = () => {
    if (!datos) return;
    try {
      generarPDFEstadoCuentaCliente(datos);
    } catch (error) {
      console.error('Error al generar el PDF del estado de cuenta:', error);
      toast.error('Error al generar el PDF');
    }
  };

  return (
    <div className="bg-gray-200 min-h-screen">
      <div className="flex justify-center py-8">
        <div className="bg-gray-100 bg-opacity-90 rounded-xl border border-gray-100 p-8 w-full max-w-7xl mx-6">
          <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <HiDocumentReport className="text-color-primario text-3xl" />
                <h1 className="font-cascadia font-bold text-2xl">Estado de Cuenta de Cliente</h1>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleExportarPDF}
                  disabled={!datos}
                  className="flex items-center gap-2 px-4 py-2 bg-color-secundario text-white rounded-xl hover:bg-opacity-90 transition-colors print:hidden disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Exportar a PDF"
                >
                  <FiDownload size={18} />
                  Exportar PDF
                </button>
                <button
                  onClick={handleActualizar}
                  disabled={cargando || !cliente.id_entidad_cliente}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors print:hidden"
                  title="Actualizar"
                >
                  <FiRefreshCw size={20} className={cargando ? 'animate-spin' : ''} />
                </button>
              </div>
            </div>

            {/* Filtros */}
            <div className="bg-white rounded-xl border border-gray-300 shadow-md p-4 print:hidden">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">Cliente</label>
                  <InputClientes
                    onClick={() => setMostrarModalClientes(true)}
                    valor={cliente.id_entidad_cliente}
                    nombreCliente={cliente.nombre}
                    placeholder="Buscar cliente..."
                    editable={true}
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Desde</label>
                  <input
                    type="date"
                    value={filtros.fecha_desde}
                    onChange={(e) => handleFiltroChange('fecha_desde', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-400 rounded-lg focus:ring-2 focus:ring-color-primario focus:border-color-primario transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Hasta</label>
                  <input
                    type="date"
                    value={filtros.fecha_hasta}
                    onChange={(e) => handleFiltroChange('fecha_hasta', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-400 rounded-lg focus:ring-2 focus:ring-color-primario focus:border-color-primario transition-colors"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <input
                  type="checkbox"
                  id="solo_con_comprobante"
                  checked={filtros.solo_con_comprobante}
                  onChange={(e) => handleFiltroChange('solo_con_comprobante', e.target.checked)}
                  className="h-4 w-4 text-color-primario border-gray-400 rounded focus:ring-color-primario"
                />
                <label htmlFor="solo_con_comprobante" className="text-sm text-gray-700 cursor-pointer">
                  Solo facturas con comprobante fiscal (NCF) y sus pagos/notas de crédito
                </label>
              </div>
            </div>

            {cargando && <SpinnerCargando />}

            {!cargando && !datos && (
              <div className="bg-white rounded-xl border border-gray-300 shadow-md p-12 text-center">
                <p className="text-gray-500 text-lg">Seleccione un cliente para ver su estado de cuenta</p>
              </div>
            )}

            {!cargando && datos && (
              <>
                {/* DATOS DEL CLIENTE */}
                <div className="bg-white rounded-xl border border-gray-300 shadow-md p-6">
                  <h2 className="font-cascadia text-lg font-bold text-gray-800 mb-2">{datos.cliente.razon_social}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm text-gray-600">
                    {datos.cliente.rnc && <p>RNC/Cédula: {datos.cliente.rnc}</p>}
                    {datos.cliente.telefono && <p>Teléfono: {datos.cliente.telefono}</p>}
                    {datos.cliente.email && <p>Email: {datos.cliente.email}</p>}
                  </div>
                </div>

                {/* RESUMEN */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <TarjetaMetrica
                    titulo="Total Facturado"
                    valor={`RD$ ${formatMonto(datos.resumen.total_facturado)}`}
                    colorBorde="border-blue-500"
                    colorTexto="text-blue-700"
                  />
                  <TarjetaMetrica
                    titulo="Total Pagado"
                    valor={`RD$ ${formatMonto(datos.resumen.total_pagado)}`}
                    colorBorde="border-green-500"
                    colorTexto="text-green-700"
                  />
                  <TarjetaMetrica
                    titulo="Balance Pendiente"
                    valor={`RD$ ${formatMonto(datos.resumen.total_pendiente)}`}
                    colorBorde="border-orange-500"
                    colorTexto="text-orange-700"
                  />
                  <TarjetaMetrica
                    titulo="Balance Vencido"
                    valor={`RD$ ${formatMonto(datos.resumen.total_vencido)}`}
                    colorBorde="border-red-500"
                    colorTexto="text-red-700"
                  />
                </div>

                {/* ANTIGÜEDAD DE CARTERA */}
                {datos.antiguedad_cartera.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="font-cascadia text-xl font-bold text-gray-800">Antigüedad de Cartera</h2>
                    <div className="bg-white rounded-xl border border-gray-300 shadow-md p-6">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Rango</th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">Facturas</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Monto</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {datos.antiguedad_cartera.map((a, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm font-medium text-gray-900 capitalize">{a.rango}</td>
                                <td className="px-4 py-3 text-sm text-gray-600 text-center">{a.cantidad}</td>
                                <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">RD$ {formatMonto(a.total)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* DETALLE CRONOLÓGICO (LIBRO MAYOR) */}
                <div className="space-y-4">
                  <h2 className="font-cascadia text-xl font-bold text-gray-800">Detalle de Movimientos</h2>
                  <div className="bg-white rounded-xl border border-gray-300 shadow-md p-6">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Fecha</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Tipo</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Documento</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Cargo</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Abono</th>
                            <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Balance</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {datos.movimientos.map((m, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-600">{formatFecha(m.fecha)}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{ETIQUETAS_TIPO[m.tipo] || m.tipo}</td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">{m.documento}</td>
                              <td className="px-4 py-3 text-sm text-gray-900 text-right">{m.cargo > 0 ? `RD$ ${formatMonto(m.cargo)}` : '-'}</td>
                              <td className="px-4 py-3 text-sm text-gray-900 text-right">{m.abono > 0 ? `RD$ ${formatMonto(m.abono)}` : '-'}</td>
                              <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">RD$ {formatMonto(m.balance)}</td>
                            </tr>
                          ))}
                          {datos.movimientos.length === 0 && (
                            <tr>
                              <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                                Sin movimientos en el período seleccionado
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal para seleccionar cliente */}
      <PlantillaModal
        mostrar={mostrarModalClientes}
        onClose={() => setMostrarModalClientes(false)}
        titulo="Seleccionar Cliente"
      >
        <TablaEntidad
          tipoEntidad={1}
          descripcion="Seleccione un cliente"
          setEntidadSeleccionada={handleClienteSeleccionado}
        />
      </PlantillaModal>
    </div>
  );
}
